import React from "react"

import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import {
  getMetricColorForMetric,
  MetricDistributionChart,
  MiniDistributionChart,
  ViolinPlot,
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"
import { Input } from "@coval/ui"

import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { formatNumber } from "@/utils/formatting/format-number"

import { SingleFloat } from "../../components/single/SingleFloat"

/**
 * Type base class for float numeric output metrics.
 *
 * Covers both LLM-based numerical metrics and audio/signal metrics that
 * produce float outputs (latency, WPM, volume, etc.). No metric-specific
 * logic lives here — that belongs in the specific metric class.
 *
 * Set `metricScale` (e.g. 100 for percentage metrics) to have all
 * display methods automatically multiply raw float values before rendering.
 */
export class NumericalMetricRecord extends MetricRecord {
  /** Multiply raw float_value by this factor before display. Default 1 (no scaling). */
  metricScale = 1

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private get _unitSuffix(): string {
    if (!this.unit) {
      return ""
    }
    return this.unit === "%" ? "%" : ` ${this.unit}`
  }

  private get _aggregateUnitSuffix(): string {
    if (!this.aggregate_units) {
      return ""
    }
    return this.aggregate_units === "%" ? "%" : ` ${this.aggregate_units}`
  }

  private _scaleValues(mos: RunMetricOutput[]): number[] {
    return mos
      .map((m) => m.float_value)
      .filter((v): v is number => v != null && Number.isFinite(v))
      .map((v) => v * this.metricScale)
  }

  protected _getNumericValuesForAggregation(
    metricOutputs: RunMetricOutput[],
  ): number[] {
    return this._scaleValues(metricOutputs)
  }

  // ─── Comparison ──────────────────────────────────────────────────────────

  compareValues(
    value1: { float_value?: number | null },
    value2: { float_value?: number | null },
  ): boolean {
    return value1.float_value === value2.float_value
  }

  // ─── Format hook ─────────────────────────────────────────────────────────

  /** True when the metric unit indicates seconds (e.g. latency, duration). Used to pick fixed two-decimal formatting. */
  protected get _isSecondsUnit(): boolean {
    return this.unit === "s" || this.aggregate_units === "s"
  }

  /**
   * Format a single float value for display in the table cell and simulation
   * card badge. Override to customize number formatting (e.g. mm:ss for
   * duration metrics). The unit suffix is appended separately by the caller.
   * Uses fixed two decimals for seconds-unit metrics and when metricScale !== 1; otherwise formatNumber.
   * @param preScaled When true, v is already scaled (e.g. from _scaleValues); do not multiply by metricScale again.
   */
  protected _formatValue(v: number, preScaled = false): string {
    const valueToFormat = preScaled ? v : v * this.metricScale
    const useFixedTwo = this.metricScale !== 1 || this._isSecondsUnit
    if (useFixedTwo) {
      return valueToFormat.toFixed(2)
    }
    return formatNumber(valueToFormat).toString()
  }

  // ─── Simulation Card ──────────────────────────────────────────────────────

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const color = getMetricColorForMetric(mo.float_value ?? -1, this)
    const displayValue =
      mo.float_value != null ? this._formatValue(mo.float_value) : "--"

    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses[color] ?? yesNoBadgeColorClasses.default}`}
      >
        {displayValue}
        {this._unitSuffix}
      </span>
    )
  }

  // ─── Single output ────────────────────────────────────────────────────────

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    if (this.metricScale !== 1) {
      return (
        <SingleFloat
          mo={mo}
          units={this.aggregate_units}
          formatFn={(v) => (v * this.metricScale).toFixed(2)}
          metric={this}
        />
      )
    }
    return (
      <SingleFloat
        mo={mo}
        units={this.aggregate_units}
        formatFn={(v) => this._formatValue(v)}
        metric={this}
      />
    )
  }

  // ─── Overview / run review ────────────────────────────────────────────────

  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const boxGroups = allGroupKeys
      .map((key) => {
        const values = this._scaleValues(groups.get(key) ?? [])
        if (values.length === 0) {
          return null
        }
        return {
          id: key,
          label: getCompareByDisplayLabel(key, compareBy),
          values,
          color: compareBy.colors[key] ?? "hsl(var(--heroui-primary))",
        }
      })
      .filter((d): d is NonNullable<typeof d> => d !== null)
    if (boxGroups.length === 0) {
      return null
    }
    return (
      <ViolinPlot
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        groups={boxGroups}
        units={this.aggregate_units?.trim() || ""}
        height={200}
      />
    )
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    if (!mo[0]) {
      return null
    }
    const valid = mo.filter((m) => m.float_value !== null)
    if (valid.length === 0) {
      return null
    }
    const average =
      valid.map((m) => m.float_value as number).reduce((a, b) => a + b, 0) /
      valid.length
    const scaled = average * this.metricScale
    return this._formatValue(scaled, true) + this._aggregateUnitSuffix
  }

  getOverviewNode(
    mo: RunMetricOutput[],
    options?: {
      highlightValue?: number | string | null
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode | null {
    if (!mo[0]) {
      return null
    }
    const showMiniChart = mo.filter((m) => m.float_value !== null).length > 1
    if (!showMiniChart) {
      return null
    }
    const xAxisLabel = this.aggregate_units
      ? `${this.metric_display_name} (${this.aggregate_units})`
      : this.metric_display_name
    const highlightValue = options?.highlightValue
    const scaledHighlight =
      this.metricScale !== 1 && typeof highlightValue === "number"
        ? highlightValue * this.metricScale
        : highlightValue
    if (this.metricScale !== 1) {
      return (
        <MiniDistributionChart
          metricOutputs={mo}
          xAxisLabel={xAxisLabel}
          extractFloatValues={(mos) => this._scaleValues(mos)}
          highlightValue={scaledHighlight}
        />
      )
    }
    return (
      <MiniDistributionChart
        metricOutputs={mo}
        xAxisLabel={xAxisLabel}
        highlightValue={scaledHighlight}
        binWidthSeconds={this._isSecondsUnit ? 1 : undefined}
      />
    )
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: ChartBarFilter) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    const highlightValue = context?.highlightValue
    const scaledHighlight =
      this.metricScale !== 1 && typeof highlightValue === "number"
        ? highlightValue * this.metricScale
        : highlightValue
    if (this.metricScale !== 1) {
      return (
        <MetricDistributionChart
          metricOutputs={metricOutputs}
          metricRecord={this}
          extractFloatValues={(mos) => this._scaleValues(mos)}
          formatStatValue={(v) => v.toFixed(2)}
          highlightValue={scaledHighlight}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      )
    }
    return (
      <MetricDistributionChart
        metricOutputs={metricOutputs}
        metricRecord={this}
        highlightValue={scaledHighlight}
        onBarClick={context?.onBarClick}
        activeFilters={context?.activeFilters}
      />
    )
  }

  // ─── Alert forms ─────────────────────────────────────────────────────────

  renderAggregateAlertForm(
    threshold: string,
    onThresholdChange: (value: string) => void,
  ): React.ReactNode {
    return (
      <Input
        placeholder="Threshold"
        value={threshold}
        onChange={(e) => onThresholdChange(e.target.value)}
        type="number"
        className="flex-1"
      />
    )
  }
}
